import { useEffect, useMemo, useState } from "react";
import { DomShell } from "./_gameKit";
import AdReward from "./_adReward";

/** Draw a continuous line touching every cell of a 4x5 grid. */
const ROWS = 5, COLS = 4;

function levelForSeed(seed) {
  // Simple seed picks a start cell for variety; all cells always required
  const start = [seed % ROWS, (seed * 7) % COLS];
  return { start, rows: ROWS, cols: COLS };
}

export default function JixxeoOneLine() {
  const [seed, setSeed] = useState(1);
  const level = useMemo(() => levelForSeed(seed), [seed]);
  const [path, setPath] = useState([[level.start[0], level.start[1]]]);
  const [dragging, setDragging] = useState(false);
  const total = level.rows * level.cols;
  const done = path.length === total;

  const inPath = (r, c) => path.some(([pr, pc]) => pr === r && pc === c);
  const isNeighbor = (r, c, last) => Math.abs(r - last[0]) + Math.abs(c - last[1]) === 1;

  const enter = (r, c) => {
    if (done) return;
    const last = path[path.length - 1];
    if (r === last[0] && c === last[1]) return;
    if (path.length >= 2) {
      const prev = path[path.length - 2];
      if (prev[0] === r && prev[1] === c) { setPath((p) => p.slice(0, -1)); return; }
    }
    if (!isNeighbor(r, c, last)) return;
    if (inPath(r, c)) return;
    setPath((p) => [...p, [r, c]]);
  };

  const restart = () => { setSeed((s) => s + 1); };
  useEffect(() => { setPath([[level.start[0], level.start[1]]]); }, [level]);

  return (
    <DomShell title="Jixxeo One Line" accent="#00F0FF"
      chips={[["Filled", `${path.length}/${total}`], done ? ["Status", "Solved ✓"] : ["Status", "…"]]}
      instructions="Drag from cell to cell to fill every square with one line"
    >
      <div className="grid gap-2 mx-auto select-none touch-none"
        style={{ gridTemplateColumns: `repeat(${level.cols}, 1fr)`, maxWidth: 320 }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onTouchEnd={() => setDragging(false)}
      >
        {Array.from({ length: level.rows }).map((_, r) => (
          Array.from({ length: level.cols }).map((__, c) => {
            const on = inPath(r, c);
            const isLast = path[path.length - 1][0] === r && path[path.length - 1][1] === c;
            const isStart = r === level.start[0] && c === level.start[1];
            const bg = on
              ? isLast ? "#FFE300" : isStart ? "#39FF14" : "#00F0FF"
              : "rgba(255,255,255,0.05)";
            const glow = on ? `0 0 16px ${isLast ? "#FFE300" : "#00F0FF"}` : "none";
            return (
              <div key={`${r}-${c}`}
                onMouseDown={() => { setDragging(true); enter(r, c); }}
                onMouseEnter={() => dragging && enter(r, c)}
                onTouchStart={() => { setDragging(true); enter(r, c); }}
                onTouchMove={(e) => {
                  const t = e.touches[0];
                  const el = document.elementFromPoint(t.clientX, t.clientY);
                  if (el && el.dataset && el.dataset.cell) {
                    const [rr, cc] = el.dataset.cell.split(",").map(Number);
                    if (dragging) enter(rr, cc);
                  }
                }}
                data-cell={`${r},${c}`}
                className="aspect-square rounded-xl border border-white/10"
                style={{ background: bg, boxShadow: glow }}
              />
            );
          })
        ))}
      </div>
      <div className="text-center mt-4">
        {done && <div className="mb-3 flex justify-center"><AdReward game="jixxeo-one-line" compact /></div>}
        <button onClick={restart}
          className="mono text-[10px] uppercase tracking-widest bg-[#00F0FF] hover:bg-white text-black font-black px-5 py-2 rounded-full">
          {done ? "Next puzzle" : "Restart"}
        </button>
      </div>
    </DomShell>
  );
}
